import { useRef, useEffect, useState, FormEvent } from 'react';
import { personalInfo, socialLinks } from '../../../mocks/portfolio';

const FORM_URL = 'https://readdy.ai/api/form/d72er7cjuufb4jdbl2pg';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const ContactSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;
    setFormStatus('submitting');
    try {
      const formData = new URLSearchParams();
      const data = new FormData(form);
      data.forEach((value, key) => formData.append(key, value.toString()));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const contactCards = [
    { icon: 'ri-mail-send-line', label: '邮箱', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: 'ri-wechat-line', label: '微信号', value: personalInfo.wechat, href: '#contact' },
  ];

  return (
    <section id="contact" ref={sectionRef} className="section-padding bg-stone-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 mb-16 text-center">
          <span className="text-xs uppercase tracking-widest text-orange-500 font-medium">Get In Touch</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-2 text-gray-900 dark:text-white">
            联系
            <span className="font-serif italic text-gradient ml-2">我</span>
          </h2>
          <p className="text-sm text-gray-400 mt-3 max-w-md mx-auto">无论是项目合作、职位机会还是技术交流，都欢迎随时联系</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-stretch">
          {/* Left: Info */}
          <div className="lg:col-span-2 flex flex-col gap-6 min-h-[600px]">
            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-100">
              <div className="flex flex-col gap-4">
                {contactCards.map((card) => (
                  <a
                    key={card.label}
                    href={card.href}
                    rel="nofollow"
                    className="group flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-orange-300 dark:hover:border-orange-700 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-50 dark:bg-orange-900/20 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/40 transition-colors flex-shrink-0">
                      <i className={`${card.icon} text-orange-500 text-xl`} />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">{card.label}</div>
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover:text-orange-500 transition-colors">{card.value}</div>
                    </div>
                    <i className="ri-arrow-right-line ml-auto text-gray-300 dark:text-gray-600 group-hover:text-orange-400 transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">关注我</h4>
                <div className="flex gap-3">
                  {socialLinks.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      aria-label={s.name}
                      className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-orange-400 hover:text-orange-500 hover:-translate-y-1 transition-all cursor-pointer"
                    >
                      <i className={`${s.icon} text-base`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-300 flex-1">
              <div className="h-full p-6 bg-gradient-to-br from-orange-500 to-rose-500 rounded-2xl text-white flex flex-col justify-between min-h-[140px]">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    <span className="text-sm font-medium">现在开放合作</span>
                  </div>
                  <p className="text-xs opacity-80 leading-relaxed">
                    正在寻找 AI 产品方向的全职 / 顾问 / 兼职机会，欢迎交流！
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs opacity-60">
                  <i className="ri-map-pin-line" />
                  <span>北京，中国</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-150 lg:col-span-3 flex flex-col min-h-[600px]">
            <div className="flex-1 bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-8 flex flex-col">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">发送消息</h3>
              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center flex-1 py-8 gap-4">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <i className="ri-check-line text-green-500 text-3xl" />
                  </div>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">消息发送成功！</h4>
                  <p className="text-sm text-gray-400 text-center">感谢你的联系，我会在 1-2 个工作日内回复你</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-2 text-sm text-orange-500 hover:underline cursor-pointer"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="flex flex-col flex-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">姓名 <span className="text-rose-500">*</span></label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="你的名字"
                        className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 dark:focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">邮箱 <span className="text-rose-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 dark:focus:border-orange-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">合作类型</label>
                    <select
                      name="type"
                      className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-orange-400 dark:focus:border-orange-500 transition-colors cursor-pointer"
                    >
                      <option value="project">项目合作</option>
                      <option value="job">职位机会</option>
                      <option value="consulting">顾问咨询</option>
                      <option value="chat">技术交流</option>
                      <option value="other">其他</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">主题</label>
                    <input
                      type="text"
                      name="subject"
                      placeholder="简单描述合作主题"
                      className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 dark:focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      消息内容 <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="请详细描述你的想法、项目背景或合作需求..."
                      className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-stone-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 dark:focus:border-orange-500 transition-colors resize-none"
                    />
                    <div className={`text-right text-xs mt-1 ${charCount > 450 ? 'text-rose-500' : 'text-gray-400'}`}>
                      {charCount}/500
                    </div>
                  </div>

                  {formStatus === 'error' && (
                    <p className="text-xs text-rose-500 mb-4 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      发送失败，请检查网络后重试
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting' || charCount > 500}
                    className="mt-auto w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-semibold text-sm hover:bg-orange-500 dark:hover:bg-orange-500 dark:hover:text-white transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        发送中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        发送消息
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
